import { SIN_TABLE, SIN_N, ATAN_TABLE, ATAN_N } from './tables/trig.gen';

/* ═══════════════════════════════════════════════════════════════════════════
   Trigonométrie DÉTERMINISTE.

   Math.sin, Math.cos, Math.atan2 et consorts ne sont pas spécifiés au bit près
   par ECMAScript. Deux navigateurs donnent deux résultats, et V8 a déjà changé
   le sien entre deux versions. Un projectile dont l'angle en dépend fait
   diverger le replay CHEZ LE JOUEUR — jamais sur la machine du dev.

   Ici tout se ramène à : division, comparaison, lecture de table, interpolation
   linéaire. Que des opérations IEEE-754 à arrondi correct, donc identiques
   partout. La table elle-même est commitée (PLAN.md §3.5).

   La règle ESLint interdit Math.<transcendante> dans src/sim/ ; ce module est
   le remplacement autorisé.
   ═══════════════════════════════════════════════════════════════════════════ */

export const PI = 3.141592653589793;
export const TAU = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
export const DEG = 0.017453292519943295;

const SIN_SCALE = SIN_N / TAU;
const ATAN_SCALE = ATAN_N;

/** sin déterministe. Précision ≈ 1,2e-6 rad — trois ordres de grandeur sous ce
 *  qu'un projectile peut exprimer à l'écran. */
export function sin(a: number): number {
  let t = a * SIN_SCALE;
  // Repli dans [0, SIN_N). Math.floor est autorisé : arrondi exact.
  t -= Math.floor(t / SIN_N) * SIN_N;
  if (t < 0) t = 0;
  else if (t >= SIN_N) t = SIN_N - 1e-6;
  const i = Math.floor(t);
  const f = t - i;
  const s0 = SIN_TABLE[i]!;
  const s1 = SIN_TABLE[i + 1]!;
  return s0 + (s1 - s0) * f;
}

export function cos(a: number): number {
  return sin(a + HALF_PI);
}

/** atan(t) pour t dans [0, 1]. Usage interne d'atan2. */
function atanUnit(t: number): number {
  const x = t * ATAN_SCALE;
  const i = Math.floor(x);
  if (i >= ATAN_N) return ATAN_TABLE[ATAN_N]!;
  const f = x - i;
  const a0 = ATAN_TABLE[i]!;
  const a1 = ATAN_TABLE[i + 1]!;
  return a0 + (a1 - a0) * f;
}

/** atan2 déterministe, replié par octant sur une table de [0, 1].
 *
 *  C'est LA fonction qui a motivé la liste blanche ESLint : un missile à tête
 *  chercheuse s'écrit avec atan2 dans neuf cas sur dix, et une liste noire
 *  visant `random|sin|cos|pow` l'aurait laissée passer sans un mot. */
export function atan2(y: number, x: number): number {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (ax === 0 && ay === 0) return 0;

  let a: number;
  if (ax >= ay) a = atanUnit(ay / ax);
  else a = HALF_PI - atanUnit(ax / ay);

  if (x < 0) a = PI - a;
  return y < 0 ? -a : a;
}

/** Normalise un angle dans [-π, π]. Utile pour une rotation par le plus court
 *  chemin (tourelles, têtes chercheuses). */
export function wrapAngle(a: number): number {
  let t = a + PI;
  t -= Math.floor(t / TAU) * TAU;
  return t - PI;
}

/** Distance au carré. On évite systématiquement sqrt dans les tests de
 *  collision : comparer des carrés donne le même verdict pour zéro racine. */
export function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Rapprochement exponentiel indépendant du pas de temps.
 *  `tau` est la constante de temps en secondes : après tau, ~63 % du chemin
 *  est parcouru. C'est ce qui donne au vaisseau son inertie (PLAN.md §6.2).
 *
 *  `Math.exp` étant interdite ici (implementation-defined), on la reconstruit
 *  à partir d'additions, de multiplications et de divisions — toutes exactes
 *  au sens IEEE-754, donc identiques sur tous les moteurs.
 *
 *  Deux étapes :
 *    1. réduction d'argument par halvings successifs, jusqu'à x ≤ 0,5
 *    2. série de Taylor à l'ordre 5 (schéma de Horner), puis k élévations au
 *       carré pour défaire la réduction
 *
 *  Erreur mesurée ≈ 1,5e-5 sur le domaine réel (tau de 14 à 100 ms, pas de
 *  8,3 ms), et le test `math.test.ts` la vérifie contre Math.exp — c'est lui
 *  qui garantit que ce commentaire reste vrai. */
export function approachFactor(dt: number, tau: number): number {
  if (tau <= 0) return 1;
  let x = dt / tau;
  if (x <= 0) return 0;
  if (x > 16) return 1; // e^-16 ≈ 1e-7 : indiscernable de 1

  let k = 0;
  while (x > 0.5) {
    x *= 0.5;
    k++;
  }

  const ex =
    1 + x * (1 + x * (0.5 + x * (1 / 6 + x * (1 / 24 + x * (1 / 120)))));
  let e = 1 / ex;
  for (let i = 0; i < k; i++) e *= e;

  return 1 - e;
}
